import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import dompurify from 'dompurify';
import { marked } from 'marked';
import { useLocation } from 'react-router-dom';
import useExaone from 'hooks/useExaone';
import { EXAONE_MAX_TEXT_LENGTH } from 'services/fetcher/exaoneChatbot';

const SCROLL_DOWN_THRESHOLD = 40;

const SUGGESTIONS = ['LG의 AI 기술을 알려줘', 'LG의 피지컬 AI를 소개해줘', '가을에 읽기 좋은 도서 추천해줘'];

const SUGGESTION_HIDE_ORDER = [1, 2, 0];

const MOBILE_MIN_SUGGESTIONS = 2;

export const MOBILE_MAX_WIDTH = 1023;

const TEXT_SIZES = ['L', 'M', 'S'] as const;

type TextSize = typeof TEXT_SIZES[number];

/** 답변 내 링크는 새 창으로 열기 */
const markdownRenderer = new marked.Renderer();
const renderLink = markdownRenderer.link.bind(markdownRenderer);
markdownRenderer.link = (href, title, text) =>
  renderLink(href, title, text).replace('<a ', '<a target="_blank" rel="noopener noreferrer" ');

/** EXAONE 답변(마크다운)을 XSS 정화된 HTML로 변환 */
const renderMarkdown = (content: string) =>
  dompurify.sanitize(marked.parse(content.trim(), { renderer: markdownRenderer, breaks: true }), {
    ADD_ATTR: ['target'],
  });

const ExaoneMarkdown = React.memo(({ content }: { content: string }) => (
  <div className="exaone_chat-bubble is-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
));

interface ExaoneChatProps {
  isOpen: boolean;
  isHidden?: boolean;
  onClose: () => void;
  style?: React.CSSProperties;
}

const ExaoneChat = ({ isOpen, isHidden = false, onClose, style }: ExaoneChatProps) => {
  const { send, clear, messages, answer, isLoading, errorMessage } = useExaone();
  const { pathname } = useLocation();
  const [input, setInput] = useState('');
  const [isStarted, setIsStarted] = useState(false);
  const [textSize, setTextSize] = useState<TextSize>('M');
  const [isTextSizeOpen, setIsTextSizeOpen] = useState(false);
  const [isShowDown, setIsShowDown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isForceExpanded, setIsForceExpanded] = useState(false);
  const isFullScreen = isExpanded || isForceExpanded;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const introRef = useRef<HTMLDivElement | null>(null);
  const suggestionsRef = useRef<HTMLUListElement | null>(null);
  const textSizeRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const isAtBottomRef = useRef(true);

  useEffect(() => {
    const el = inputRef.current;

    if (!el) {
      return;
    }

    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  useEffect(() => {
    if (!isTextSizeOpen) {
      return undefined;
    }

    const onClickOutside = (e: MouseEvent) => {
      if (textSizeRef.current && !textSizeRef.current.contains(e.target as Node)) {
        setIsTextSizeOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);

    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isTextSizeOpen]);

  const onSelectTextSize = (size: TextSize) => {
    setTextSize(size);
    setIsTextSizeOpen(false);
  };

  useEffect(() => {
    const intro = introRef.current;
    const list = suggestionsRef.current;

    if (!isOpen || isStarted || !intro || !list) {
      return undefined;
    }

    const fitSuggestions = () => {
      const items = Array.from(list.children) as HTMLElement[];
      const isMobile = window.innerWidth <= MOBILE_MAX_WIDTH;
      const minCount = isMobile ? MOBILE_MIN_SUGGESTIONS : 0;
      let visibleCount = items.length;

      list.style.display = '';
      items.forEach(item => {
        item.style.display = '';
      });

      SUGGESTION_HIDE_ORDER.forEach(index => {
        if (intro.scrollHeight > intro.clientHeight && items[index] && visibleCount > minCount) {
          items[index].style.display = 'none';
          visibleCount -= 1;
        }
      });

      if (visibleCount === 0) {
        list.style.display = 'none';
      }

      if (isMobile && !isFullScreen && intro.scrollHeight > intro.clientHeight) {
        setIsForceExpanded(true);
      }
    };

    const observer = new ResizeObserver(fitSuggestions);
    observer.observe(intro);

    return () => observer.disconnect();
  }, [isOpen, isStarted, isFullScreen]);

  const scrollToBottom = () => {
    isAtBottomRef.current = true;

    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [isOpen, isStarted]);

  useEffect(() => {
    if (isAtBottomRef.current || messages[messages.length - 1]?.role === 'user') {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom();
    }
  }, [answer, isLoading]);

  const onScroll = () => {
    if (!scrollRef.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAwayFromBottom = scrollHeight - scrollTop - clientHeight > SCROLL_DOWN_THRESHOLD;
    isAtBottomRef.current = !isAwayFromBottom;
    setIsShowDown(isAwayFromBottom);
  };

  const onSend = () => {
    const content = input.trim();

    if (!content || isLoading) {
      return;
    }

    if (send(content)) setInput('');
  };

  const reset = () => {
    clear();
    setInput('');
    setIsStarted(false);
    setIsTextSizeOpen(false);
    setIsShowDown(false);
    isAtBottomRef.current = true;
  };

  useEffect(() => {
    reset();
  }, [pathname]);

  const onSuggestion = (content: string) => {
    setIsStarted(true);
    send(content);
  };

  useEffect(() => {
    if (!isOpen) {
      setIsExpanded(false);
      setIsForceExpanded(false);
      return undefined;
    }

    document.body.classList.add('no-scroll-mobile');

    return () => document.body.classList.remove('no-scroll-mobile');
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`exaone_chat${textSize === 'L' ? ' is-large' : ''}${textSize === 'S' ? ' is-small' : ''}${isFullScreen ? ' is-expanded' : ''}${isHidden ? ' is-hidden' : ''}`}
      style={style}
      onDragStart={e => e.preventDefault()}
    >
      <div className="exaone_chat-header">
        <div className="exaone_chat-badge" onClick={reset}>
          <span className="exaone_chat-logo">
            <img src="/assets/images/icon/ico-exaone-logo.png" alt="" />
          </span>
          <p className="exaone_chat-title">
            LG LIFE <b>·</b> <b>EXAONE</b>
          </p>
        </div>
        <div className="exaone_chat-actions">
          {!isForceExpanded && (
            <button type="button" className="exaone_chat-expand is-mobile" onClick={() => setIsExpanded(!isExpanded)}>
              <img
                src={`/assets/images/icon/ico-exaone-${isExpanded ? 'shrink' : 'expand'}.svg`}
                alt={isExpanded ? '축소' : '전체화면'}
              />
            </button>
          )}
          <button type="button" className="exaone_chat-close" onClick={onClose}>
            <img src="/assets/images/icon/ico-action-24-close.svg" alt="닫기" />
          </button>
        </div>
      </div>

      {!isStarted ? (
        <div className="exaone_chat-body is-intro">
          <div className="exaone_chat-intro" ref={introRef}>
            <div className="exaone_chat-hero">
              <span className="exaone_chat-hero_logo">
                <img src="/assets/images/icon/ico-exaone-logo.png" alt="" />
              </span>
              <p className="exaone_chat-hero_title">
                필요한 정보나, 궁금한 것
              </p>
              <p className="exaone_chat-hero_desc">무엇이든 물어보세요.</p>
            </div>

            <ul className="exaone_chat-suggestions" ref={suggestionsRef}>
              {SUGGESTIONS.map(suggestion => (
                <li key={suggestion}>
                  <button type="button" className="exaone_chat-suggestion" onClick={() => onSuggestion(suggestion)}>
                    <span className="exaone_chat-logo">
                      <img src="/assets/images/icon/ico-exaone-logo.png" alt="" />
                    </span>
                    {suggestion}
                  </button>
                </li>
              ))}
            </ul>

            <div className="exaone_chat-start">
              <button type="button" className="exaone_chat-start_button" onClick={() => setIsStarted(true)}>
                EXAONE과 대화 시작하기
              </button>
              <p className="exaone_chat-start_desc">버튼을 누르면 대화를 시작합니다.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="exaone_chat-body">
          <div className="exaone_chat-scroll" ref={scrollRef} onScroll={onScroll}>
            <div className="exaone_chat-top">
              <span className="exaone_chat-date">{moment().format('YYYY년 M월 D일')}</span>
              <div className="exaone_chat-text_size_wrap" ref={textSizeRef}>
                <button
                  type="button"
                  className={`exaone_chat-text_size${isTextSizeOpen ? ' is-active' : ''}`}
                  aria-haspopup="listbox"
                  aria-expanded={isTextSizeOpen}
                  onClick={() => setIsTextSizeOpen(!isTextSizeOpen)}
                >
                  A<small>A</small>
                </button>
                {isTextSizeOpen && (
                  <div className="exaone_chat-text_size_menu">
                    <p className="exaone_chat-text_size_title">글자</p>
                    <ul role="listbox">
                      {TEXT_SIZES.map(size => (
                        <li key={size}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={textSize === size}
                            className={`exaone_chat-text_size_option${textSize === size ? ' is-selected' : ''}`}
                            onClick={() => onSelectTextSize(size)}
                          >
                            {size}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="exaone_chat-list">
              <div className="exaone_chat-row">
                <span className="exaone_chat-avatar">
                  <img src="/assets/images/icon/ico-exaone-bot.svg" alt="" />
                </span>
                <div className="exaone_chat-bubble">
                  {'안녕하세요, EXAONE입니다.\n궁금한 점이 있으시면 저에게 질문해 주세요!'}
                </div>
              </div>

              {messages.map((message, i) => {
                const isUser = message.role === 'user';

                return (
                  <div key={i} className={`exaone_chat-row${isUser ? ' is-user' : ''}`}>
                    {isUser ? (
                      <>
                        <div className="exaone_chat-bubble">{message.content}</div>
                        <img className="exaone_chat-avatar" src="/assets/images/icon/ico-exaone-profile.svg" alt="" />
                      </>
                    ) : (
                      <>
                        <span className="exaone_chat-avatar">
                          <img src="/assets/images/icon/ico-exaone-bot.svg" alt="" />
                        </span>
                        <ExaoneMarkdown content={message.content} />
                      </>
                    )}
                  </div>
                );
              })}

              {isLoading && (
                <div className="exaone_chat-row">
                  <span className="exaone_chat-avatar">
                    <img src="/assets/images/icon/ico-exaone-bot.svg" alt="" />
                  </span>
                  {answer ? (
                    <ExaoneMarkdown content={answer} />
                  ) : (
                    <div className="exaone_chat-bubble is-loading">
                      <span className="exaone_chat-typing" aria-hidden="true">
                        <i />
                        <i />
                        <i />
                      </span>
                      AI가 작성 중입니다.
                    </div>
                  )}
                </div>
              )}

              {Boolean(errorMessage) && <p className="exaone_chat-error">{errorMessage}</p>}
            </div>
          </div>

          {isShowDown && (
            <button type="button" className="exaone_chat-down" onClick={scrollToBottom}>
              <img src="/assets/images/icon/ico-exaone-down.svg" alt="최신 메시지로 이동" />
            </button>
          )}

          <div className="exaone_chat-input">
            <textarea
              ref={inputRef}
              rows={1}
              maxLength={EXAONE_MAX_TEXT_LENGTH}
              value={input}
              placeholder="메시지를 입력하세요..."
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  onSend();
                }
              }}
            />
            <button type="button" className="exaone_chat-send" disabled={isLoading || !input.trim()} onClick={onSend}>
              <img src="/assets/images/icon/ico-exaone-send.svg" alt="전송" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExaoneChat;
